import { GameSettingType } from "@1games-ru/region-shared/dist/enums/account/settings";
import { isNaN } from "lodash";
import { motion } from "motion/react";
import { FC, useEffect, useLayoutEffect, useMemo } from "react";
import useAppSelector from "@/core/helpers/useAppSelector";
import useGetPlayer from "@/shared/hooks/useGetPlayer";
import useInterfaceSoundCore from "@/shared/hooks/useInterfaceSoundCore";
import { Soundtrack } from "@/shared/sounds";
import SoundCore from "@/shared/widgets/SoundCore";
import { HeaderTab } from "@/domains/playerMenu/constants/headerTab";
import { SubHeaderTab } from "@/domains/playerMenu/constants/subHeaderTab";
import { playerInfoSelector } from "@/domains/player/slice";
import { usePlayerMenuStore } from "@/domains/playerMenu/store";
import { getOpacity } from "@/domains/playerMenu/helpers/getOpacity";
import useShowPlayerMenu from "@/domains/playerMenu/hooks/useShowPlayerMenu";
import PlayerMenuCharacterVehicleContainer from "@/domains/playerMenu/containers//character/vehicle/PlayerMenuCharacterVehicleContainer";
import PlayerMenuHeaderContainer from "@/domains/playerMenu/containers/PlayerMenuHeaderContainer";
import PlayerMenuPlayerInfoContainer from "@/domains/playerMenu/containers/PlayerMenuPlayerInfoContainer";
import PlayerMenuSubHeaderContainer from "@/domains/playerMenu/containers/PlayerMenuSubHeaderContainer";
import PlayerMenuCharacterDataContainer from "@/domains/playerMenu/containers/character/data/PlayerMenuCharacterDataContainer";
import PlayerMenuCharacterEstateContainer from "@/domains/playerMenu/containers/character/estate/PlayerMenuCharacterEstateContainer";
import PlayerMenuSettingsBasicContainer from "@/domains/playerMenu/containers/settings/basic/PlayerMenuSettingsBasicContainer";
import PlayerMenuSettingsChatContainer from "@/domains/playerMenu/containers/settings/chat/PlayerMenuSettingsChatContainer";
import PlayerMenuSettingsControlContainer from "@/domains/playerMenu/containers/settings/control/PlayerMenuSettingsControlContainer";
import PlayerMenuSettingsGameplayContainer from "@/domains/playerMenu/containers/settings/gameplay/PlayerMenuSettingsGameplayContainer";
import PlayerMenuSettingsSafetyContainer from "@/domains/playerMenu/containers/settings/safety/PlayerMenuSettingsSafetyContainer";
import PlayerMenuSettingsSoundContainer from "@/domains/playerMenu/containers/settings/sound/PlayerMenuSettingsSoundContainer";
import { appliedBasicSettingsSelector } from "@/domains/settings/basic/slice";
import { appliedSoundSettingsSelector } from "@/domains/settings/sound/slice";

const componentsDict: Record<HeaderTab, Partial<Record<SubHeaderTab, FC>>> = {
  [HeaderTab.Character]: {
    [SubHeaderTab.Data]: PlayerMenuCharacterDataContainer,
    [SubHeaderTab.Documents]: null,
    [SubHeaderTab.Estate]: PlayerMenuCharacterEstateContainer,
    [SubHeaderTab.Vehicle]: PlayerMenuCharacterVehicleContainer,
    [SubHeaderTab.Family]: null,
    [SubHeaderTab.Faction]: null,
    [SubHeaderTab.Business]: null,
    [SubHeaderTab.Skills]: null,
    [SubHeaderTab.Statistic]: null,
  },
  [HeaderTab.Settings]: {
    [SubHeaderTab.Basic]: PlayerMenuSettingsBasicContainer,
    [SubHeaderTab.Sound]: PlayerMenuSettingsSoundContainer,
    [SubHeaderTab.Control]: PlayerMenuSettingsControlContainer,
    [SubHeaderTab.Safety]: PlayerMenuSettingsSafetyContainer,
    [SubHeaderTab.Gameplay]: PlayerMenuSettingsGameplayContainer,
    [SubHeaderTab.Chat]: PlayerMenuSettingsChatContainer,
  },
};

export default function PlayerMenuContainer() {
  const { play } = useInterfaceSoundCore(Soundtrack.OpenModal);
  const playerInfo = useAppSelector(playerInfoSelector);
  const { getPlayer } = useGetPlayer();
  const { isShow } = useShowPlayerMenu();
  const { currHeaderTab, changeHeaderTab, headerTabs, currSubHeaderTab, setHeaderTabs } =
    usePlayerMenuStore();
  const appliedBasicSettings = useAppSelector(appliedBasicSettingsSelector);
  const appliedSoundSettings = useAppSelector(appliedSoundSettingsSelector);

  useLayoutEffect(() => {
    if (isShow) {
      play();
      getPlayer();
    }
  }, [isShow]);

  useLayoutEffect(() => {
    if (playerInfo?.character) {
      setHeaderTabs([HeaderTab.Character, HeaderTab.Settings]);
    } else {
      setHeaderTabs([HeaderTab.Settings]);
    }
  }, [playerInfo]);

  useEffect(() => {
    if (!currHeaderTab) {
      changeHeaderTab(headerTabs[0]);
    }
  }, [currHeaderTab, headerTabs]);

  useEffect(() => {
    return () => {
      changeHeaderTab(null);
    };
  }, []);

  const menuMusicOptions = useMemo(() => {
    return {
      volume: appliedSoundSettings
        ? appliedSoundSettings[GameSettingType.SettingMenuMusic] / 100
        : 0,
    };
  }, [appliedSoundSettings]);

  const Component =
    currHeaderTab && currSubHeaderTab ? componentsDict[currHeaderTab][currSubHeaderTab] : null;

  const opacity = useMemo(() => {
    const result = appliedBasicSettings
      ? getOpacity(appliedBasicSettings[GameSettingType.SettingMainMenuTransparency])
      : getOpacity(0);

    return isNaN(result) ? getOpacity(0) : result;
  }, [appliedBasicSettings]);

  if (!isShow || !appliedBasicSettings) return null;

  return (
    <div className="fixed inset-y-[--boundary-offset-y] inset-x-[--boundary-offset-x] flex flex-col justify-center items-center">
      <SoundCore trigger="mount" name={Soundtrack.AccountBackground} options={menuMusicOptions} />
      <motion.div
        initial={{ opacity: 0, translateY: "-100%" }}
        animate={{ opacity: 1, translateY: "none" }}
        transition={{ type: "spring", duration: 0.3 }}
        exit={{ opacity: 0, translateY: "-100%" }}
        className="rounded-[1rem] overflow-clip flex flex-col h-[56rem] max-h-full max-w-full aspect-[2/1] shadow-xl"
        style={{
          backgroundColor: `rgba(18, 18, 18, ${opacity})`,
        }}
      >
        <PlayerMenuHeaderContainer />
        <div className="flex items-start grow w-full">
          <PlayerMenuPlayerInfoContainer />
          <div
            className="h-full grow flex items-start border-l"
            style={{
              borderColor: `rgb(18 18 18 / ${0.5 * opacity})`,
            }}
          >
            <PlayerMenuSubHeaderContainer />

            <div className="grow h-full">{Component && <Component />}</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
